import { InviteDialog } from "@/components/admin/invite-dialog";
import { SettingsLayout } from "@/components/settings-layout";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { authClient, useSession } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Plus, RotateCcw, ShieldOff, Trash2, UserCheck, UserX } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings/team")({
	component: TeamSettingsPage,
	// Keep non-admins off the page before any UI renders. The parent /_app
	// loader already enforces "signed in + onboarded + 2FA enrolled"; this adds
	// the role check. The server-side adminProcedure middleware is still the
	// real enforcement — this is a UX sweetener to avoid a flash of admin UI.
	beforeLoad: async () => {
		const { data: session } = await authClient.getSession();
		if (session?.user?.role !== "admin") {
			throw redirect({ to: "/" });
		}
	},
});

type Role = "admin" | "user";

function TeamSettingsPage() {
	const { data: session } = useSession();

	const list = trpc.users.listAdmin.useQuery();
	const [inviteOpen, setInviteOpen] = useState(false);

	if (list.isPending) {
		return (
			<SettingsLayout>
				<SkeletonState />
			</SettingsLayout>
		);
	}
	if (list.error) {
		return (
			<SettingsLayout>
				<div className="rounded-lg border border-[#fecaca] bg-white p-6 text-sm text-[#dc2626]">
					Failed to load users: {list.error.message}
				</div>
			</SettingsLayout>
		);
	}

	const { users, pendingInvites } = list.data;
	const active = users.filter((u) => !u.deactivatedAt);
	const deactivated = users.filter((u) => u.deactivatedAt);

	return (
		<SettingsLayout>
			<div className="flex items-start justify-between">
				<div>
					<h2 className="text-lg font-semibold text-[#1e1e20]">Team</h2>
					<p className="mt-1 text-sm text-[#6b7280]">Invite new members and manage access.</p>
				</div>
				<button
					type="button"
					onClick={() => setInviteOpen(true)}
					className="inline-flex h-9 items-center gap-1.5 rounded bg-[#16a34a] px-3 text-sm font-semibold text-white hover:bg-[#15803d]"
				>
					<Plus className="h-4 w-4" strokeWidth={2} />
					Invite user
				</button>
			</div>

			{inviteOpen && (
				<InviteDialog currentUserId={session?.user.id} onClose={() => setInviteOpen(false)} />
			)}

			<UsersTable title="Active members" rows={active} currentUserId={session?.user.id} />

			{pendingInvites.length > 0 && <PendingInvitesTable rows={pendingInvites} />}

			{deactivated.length > 0 && (
				<UsersTable
					title="Deactivated"
					rows={deactivated}
					currentUserId={session?.user.id}
					isDeactivatedTable
				/>
			)}
		</SettingsLayout>
	);
}

// ─── Users table ───────────────────────────────────────────────────────

type UserRow = {
	id: string;
	name: string;
	email: string;
	role: string;
	twoFactorEnabled: boolean;
	twoFactorMethod: string | null;
	onboardedAt: Date | null;
	deactivatedAt: Date | null;
	invitedByUserId: string | null;
	createdAt: Date;
};

function UsersTable({
	title,
	rows,
	currentUserId,
	isDeactivatedTable = false,
}: {
	title: string;
	rows: UserRow[];
	currentUserId: string | undefined;
	isDeactivatedTable?: boolean;
}) {
	if (rows.length === 0) {
		return null;
	}

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white">
			<div className="border-b border-[#e5e7eb] px-6 py-3">
				<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">{title}</h2>
			</div>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-[#f3f4f6] bg-[#f8f9fb]">
						<Th>User</Th>
						<Th>Role</Th>
						<Th>2FA</Th>
						<Th>Onboarded</Th>
						<Th className="text-right">Actions</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-[#f3f4f6]">
					{rows.map((row) => (
						<UserRowView
							key={row.id}
							row={row}
							isSelf={row.id === currentUserId}
							isDeactivated={isDeactivatedTable}
						/>
					))}
				</tbody>
			</table>
		</div>
	);
}

// Which row-level destructive action is currently awaiting confirmation.
// `null` = no dialog open; otherwise the kind drives the dialog copy and the
// mutation that fires on confirm. See below for the per-kind COPY map.
type RowConfirmKind = "deactivate" | "resetTwoFactor";

const USER_ROW_CONFIRM_COPY: Record<
	RowConfirmKind,
	{ title: string; description: (email: string) => string; confirmLabel: string }
> = {
	deactivate: {
		title: "Deactivate user?",
		description: (email) =>
			`${email} will not be able to sign in. You can reactivate them from this page later.`,
		confirmLabel: "Deactivate",
	},
	resetTwoFactor: {
		title: "Reset 2FA for user?",
		description: (email) =>
			`${email} will have to re-enroll 2FA on their next login. This action is audited.`,
		confirmLabel: "Reset 2FA",
	},
};

function UserRowView({
	row,
	isSelf,
	isDeactivated,
}: {
	row: UserRow;
	isSelf: boolean;
	isDeactivated: boolean;
}) {
	const utils = trpc.useUtils();
	const updateRole = trpc.users.updateRole.useMutation({
		onSuccess: () => utils.users.listAdmin.invalidate(),
	});
	const deactivate = trpc.users.deactivate.useMutation({
		onSuccess: () => utils.users.listAdmin.invalidate(),
	});
	const reactivate = trpc.users.reactivate.useMutation({
		onSuccess: () => utils.users.listAdmin.invalidate(),
	});
	const resetTwoFactor = trpc.users.resetTwoFactor.useMutation({
		onSuccess: () => utils.users.listAdmin.invalidate(),
	});

	const [confirmKind, setConfirmKind] = useState<RowConfirmKind | null>(null);

	async function handleRoleChange(newRole: Role) {
		try {
			await updateRole.mutateAsync({ userId: row.id, role: newRole });
			toast.success("Role updated");
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : "Failed to update role");
		}
	}

	async function handleReactivate() {
		try {
			await reactivate.mutateAsync({ userId: row.id });
			toast.success("User reactivated");
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : "Failed to reactivate");
		}
	}

	// Runs on ConfirmDialog confirm — dispatches to the appropriate mutation
	// based on the pending kind. Cancel is wired via onOpenChange(false).
	async function runConfirmedAction(kind: RowConfirmKind) {
		setConfirmKind(null);
		if (kind === "deactivate") {
			try {
				await deactivate.mutateAsync({ userId: row.id });
				toast.success("User deactivated");
			} catch (err: unknown) {
				toast.error(err instanceof Error ? err.message : "Failed to deactivate");
			}
			return;
		}
		if (kind === "resetTwoFactor") {
			try {
				await resetTwoFactor.mutateAsync({ userId: row.id });
				toast.success("2FA reset");
			} catch (err: unknown) {
				toast.error(err instanceof Error ? err.message : "Failed to reset 2FA");
			}
			return;
		}
	}

	const confirmCopy = confirmKind ? USER_ROW_CONFIRM_COPY[confirmKind] : null;
	const confirmPending =
		(confirmKind === "deactivate" && deactivate.isPending) ||
		(confirmKind === "resetTwoFactor" && resetTwoFactor.isPending);

	return (
		<tr>
			<td className="px-6 py-3">
				<div className="font-medium text-[#1e1e20]">{row.name}</div>
				<div className="text-xs text-[#6b7280]">{row.email}</div>
			</td>
			<td className="px-6 py-3">
				{isSelf || isDeactivated ? (
					<span className="text-sm text-[#6b7280]">{row.role}</span>
				) : (
					<select
						value={row.role}
						onChange={(e) => handleRoleChange(e.target.value as Role)}
						disabled={updateRole.isPending}
						className="h-7 rounded border border-[#d1d5db] bg-white px-2 text-xs text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 disabled:opacity-50"
					>
						<option value="user">user</option>
						<option value="admin">admin</option>
					</select>
				)}
			</td>
			<td className="px-6 py-3">
				{row.twoFactorEnabled ? (
					<span className="inline-flex items-center gap-1 rounded bg-[#f0fdf4] px-1.5 py-0.5 text-xs font-medium text-[#166534]">
						Enrolled
						{row.twoFactorMethod ? (
							<span className="text-[#6b7280]">· {row.twoFactorMethod}</span>
						) : null}
					</span>
				) : (
					<span className="inline-flex items-center rounded bg-[#fef3c7] px-1.5 py-0.5 text-xs font-medium text-[#92400e]">
						Not set up
					</span>
				)}
			</td>
			<td className="px-6 py-3 text-xs text-[#6b7280]">
				{row.onboardedAt ? new Date(row.onboardedAt).toLocaleDateString() : "—"}
			</td>
			<td className="px-6 py-3 text-right">
				<div className="flex items-center justify-end gap-1">
					{!isSelf && !isDeactivated && row.twoFactorEnabled && (
						<RowAction
							icon={<ShieldOff className="h-3.5 w-3.5" strokeWidth={1.5} />}
							label="Reset 2FA"
							onClick={() => setConfirmKind("resetTwoFactor")}
							pending={resetTwoFactor.isPending}
						/>
					)}
					{!isSelf && !isDeactivated && (
						<RowAction
							icon={<UserX className="h-3.5 w-3.5" strokeWidth={1.5} />}
							label="Deactivate"
							onClick={() => setConfirmKind("deactivate")}
							pending={deactivate.isPending}
							danger
						/>
					)}
					{isDeactivated && (
						<RowAction
							icon={<UserCheck className="h-3.5 w-3.5" strokeWidth={1.5} />}
							label="Reactivate"
							onClick={handleReactivate}
							pending={reactivate.isPending}
						/>
					)}
				</div>
				<ConfirmDialog
					open={confirmKind !== null}
					onOpenChange={(open) => {
						if (!open) setConfirmKind(null);
					}}
					title={confirmCopy?.title ?? ""}
					description={confirmCopy ? confirmCopy.description(row.email) : ""}
					confirmLabel={confirmCopy?.confirmLabel ?? "Confirm"}
					destructive
					isPending={confirmPending}
					onConfirm={async () => {
						if (!confirmKind) return;
						await runConfirmedAction(confirmKind);
					}}
				/>
			</td>
		</tr>
	);
}

// ─── Pending invites ───────────────────────────────────────────────────

type InviteRow = {
	id: string;
	email: string;
	role: string;
	invitedByUserId: string | null;
	expiresAt: Date;
	createdAt: Date;
};

function PendingInvitesTable({ rows }: { rows: InviteRow[] }) {
	const utils = trpc.useUtils();
	const resendInvite = trpc.users.resendInvite.useMutation({
		onSuccess: () => utils.users.listAdmin.invalidate(),
	});
	const revokeInvite = trpc.users.revokeInvite.useMutation({
		onSuccess: () => utils.users.listAdmin.invalidate(),
	});

	// Which pending invite the admin has chosen to revoke. `null` = dialog
	// closed. Only one revoke can be in-flight at a time, so a single state
	// slot is enough for the whole table.
	const [pendingRevoke, setPendingRevoke] = useState<{ id: string; email: string } | null>(null);

	async function handleResend(id: string, email: string) {
		try {
			await resendInvite.mutateAsync({ id });
			toast.success(`Invite resent to ${email}`);
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : "Failed to resend");
		}
	}

	async function runConfirmedRevoke() {
		if (!pendingRevoke) return;
		const { id } = pendingRevoke;
		setPendingRevoke(null);
		try {
			await revokeInvite.mutateAsync({ id });
			toast.success("Invite revoked");
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : "Failed to revoke");
		}
	}

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white">
			<div className="border-b border-[#e5e7eb] px-6 py-3">
				<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">
					Pending invites
				</h2>
			</div>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-[#f3f4f6] bg-[#f8f9fb]">
						<Th>Email</Th>
						<Th>Role</Th>
						<Th>Expires</Th>
						<Th className="text-right">Actions</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-[#f3f4f6]">
					{rows.map((row) => (
						<tr key={row.id}>
							<td className="px-6 py-3 text-[#1e1e20]">{row.email}</td>
							<td className="px-6 py-3 text-[#6b7280]">{row.role}</td>
							<td className="px-6 py-3 text-xs text-[#6b7280]">
								{new Date(row.expiresAt).toLocaleDateString()}
							</td>
							<td className="px-6 py-3 text-right">
								<div className="flex items-center justify-end gap-1">
									<RowAction
										icon={<RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />}
										label="Resend"
										onClick={() => handleResend(row.id, row.email)}
										pending={resendInvite.isPending}
									/>
									<RowAction
										icon={<Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />}
										label="Revoke"
										onClick={() => setPendingRevoke({ id: row.id, email: row.email })}
										pending={revokeInvite.isPending}
										danger
									/>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<ConfirmDialog
				open={pendingRevoke !== null}
				onOpenChange={(open) => {
					if (!open) setPendingRevoke(null);
				}}
				title="Revoke invite?"
				description={
					pendingRevoke
						? `The invite for ${pendingRevoke.email} will be revoked and the link will no longer work.`
						: ""
				}
				confirmLabel="Revoke"
				destructive
				isPending={revokeInvite.isPending}
				onConfirm={runConfirmedRevoke}
			/>
		</div>
	);
}

// ─── Small helpers ─────────────────────────────────────────────────────

function Th({
	children,
	className = "",
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<th
			className={`px-6 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-[#6b7280] ${className}`}
		>
			{children}
		</th>
	);
}

function RowAction({
	icon,
	label,
	onClick,
	pending,
	danger = false,
}: {
	icon: React.ReactNode;
	label: string;
	onClick: () => void;
	pending: boolean;
	danger?: boolean;
}) {
	const colors = danger
		? "border-[#fecaca] text-[#dc2626] hover:bg-[#fef2f2]"
		: "border-[#d1d5db] text-[#6b7280] hover:bg-[#f8f9fb]";
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={pending}
			className={`inline-flex h-7 items-center gap-1 rounded border bg-white px-2 text-xs font-medium transition-colors disabled:opacity-50 ${colors}`}
		>
			{icon}
			{label}
		</button>
	);
}

function SkeletonState() {
	return (
		<div className="space-y-6">
			<div className="h-6 w-48 animate-pulse rounded bg-[#f3f4f6]" />
			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<div className="h-10 w-full animate-pulse rounded bg-[#f3f4f6]" />
			</div>
		</div>
	);
}
